import { Link } from "react-router-dom";
import { categories } from "@/mocks/articles";

interface ArticleCardProps {
  slug: string;
  title: string;
  category: string;
  coverImageSeq: string;
  thumbnailPrompt: string;
  publishedAt: string;
  views: number;
  isHot?: boolean;
  isBreaking?: boolean;
  variant?: "horizontal" | "vertical";
}

function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const hours = Math.floor(diff / 3600000);
  if (hours < 1) return "刚刚";
  if (hours < 24) return `${hours}小时前`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}天前`;
  return date.toLocaleDateString("zh-CN", { month: "short", day: "numeric" });
}

function formatViews(views: number): string {
  if (views >= 10000000) return `${(views / 10000000).toFixed(1)}千万`;
  if (views >= 10000) return `${(views / 10000).toFixed(0)}万`;
  return `${views}`;
}

function getCategoryName(slug: string): string {
  return categories.find((c) => c.slug === slug)?.name || "八卦";
}

const coverImageMap: Record<string, string> = {
  "cover-art-01": "https://readdy.ai/api/search-image?query=Minimalist%20dramatic%20scene%20with%20a%20single%20smartphone%20screen%20glowing%20in%20complete%20darkness%20on%20a%20wooden%20desk%2C%20the%20screen%20showing%20a%20cracked%20social%20media%20profile%20page%20with%20rapidly%20dropping%20follower%20count%20numbers%20in%20red%2C%20surrounded%20by%20scattered%20gossip%20magazine%20clippings%2C%20moody%20atmospheric%20lighting%20with%20deep%20shadows%2C%20editorial%20photography%20style%2C%20warm%20amber%20and%20deep%20charcoal%20tones%2C%20cinematic%20composition&width=800&height=600&seq=cover-art-01&orientation=landscape",
  "cover-art-02": "https://readdy.ai/api/search-image?query=Minimalist%20elegant%20wedding%20ring%20resting%20on%20a%20torn%20confidential%20document%20with%20red%20stamp%20visible%2C%20placed%20on%20white%20marble%20surface%2C%20soft%20natural%20window%20light%20from%20the%20side%20creating%20gentle%20shadows%2C%20scattered%20rose%20petals%2C%20sophisticated%20and%20mysterious%20editorial%20photography%2C%20cream%20and%20ivory%20tones%20with%20subtle%20gold%20accents&width=800&height=600&seq=cover-art-02&orientation=landscape",
  "cover-art-03": "https://readdy.ai/api/search-image?query=Minimalist%20scene%20of%20a%20shattered%20stage%20spotlight%20lying%20on%20a%20dark%20floor%20with%20broken%20glass%20fragments%20scattered%20around%2C%20a%20microphone%20fallen%20nearby%2C%20dramatic%20single%20overhead%20light%20casting%20harsh%20shadows%2C%20backstage%20atmosphere%2C%20cold%20blue%20and%20dark%20grey%20tones%2C%20editorial%20photography%20with%20high%20contrast&width=800&height=600&seq=cover-art-03&orientation=landscape",
  "cover-art-04": "https://readdy.ai/api/search-image?query=Minimalist%20top-down%20view%20of%20a%20calculator%2C%20scattered%20tax%20forms%20with%20red%20ink%20marks%2C%20and%20a%20stack%20of%20money%20partially%20covered%20by%20an%20official%20government%20seal%20document%2C%20arranged%20on%20a%20dark%20grey%20desk%20surface%2C%20harsh%20fluorescent%20lighting%20from%20above%20creating%20stark%20shadows%2C%20cold%20and%20clinical%20atmosphere&width=800&height=600&seq=cover-art-04&orientation=landscape",
  "cover-art-05": "https://readdy.ai/api/search-image?query=Minimalist%20scene%20of%20a%20wedding%20ring%20sitting%20alone%20on%20a%20hotel%20nightstand%20next%20to%20a%20room%20key%20card%2C%20soft%20diffused%20window%20light%20filtering%20through%20sheer%20curtains%2C%20background%20showing%20a%20blurred%20unmade%20hotel%20bed%2C%20melancholic%20and%20intimate%20atmosphere%2C%20editorial%20photography%20with%20muted%20warm%20tones%20and%20subtle%20teal%20shadows&width=800&height=600&seq=cover-art-05&orientation=landscape",
  "cover-art-06": "https://readdy.ai/api/search-image?query=Minimalist%20close-up%20of%20a%20pen%20lying%20across%20a%20heavily%20redacted%20contract%20document%20with%20large%20black%20marker%20lines%20crossing%20out%20paragraphs%2C%20dramatic%20side%20lighting%20from%20a%20desk%20lamp%20creating%20strong%20contrast%2C%20ink%20splatters%20on%20white%20paper%2C%20tense%20and%20confrontational%20atmosphere%2C%20editorial%20photography%20in%20stark%20black%20and%20white%20with%20warm%20desk%20lamp%20glow&width=800&height=600&seq=cover-art-06&orientation=landscape",
  "cover-art-07": "https://readdy.ai/api/search-image?query=Minimalist%20backstage%20view%20of%20a%20television%20studio%20control%20room%20through%20a%20slightly%20opened%20door%2C%20showing%20multiple%20monitor%20screens%20displaying%20stage%20performances%2C%20a%20clipboard%20with%20script%20visible%20resting%20on%20the%20console%2C%20cables%20and%20equipment%20in%20the%20foreground%2C%20dim%20blue%20ambient%20lighting%20from%20monitors%2C%20mysterious%20atmosphere&width=800&height=600&seq=cover-art-07&orientation=landscape",
  "cover-art-08": "https://readdy.ai/api/search-image?query=Minimalist%20overhead%20shot%20of%20a%20transparent%20empty%20donation%20box%20placed%20on%20a%20rustic%20wooden%20school%20desk%20in%20a%20dimly%20lit%20classroom%2C%20chalkboard%20with%20faded%20writing%20in%20background%2C%20a%20single%20ray%20of%20sunlight%20streaming%20through%20a%20dusty%20window%20illuminating%20the%20empty%20box%2C%20poignant%20and%20revealing%20atmosphere&width=800&height=600&seq=cover-art-08&orientation=landscape",
  "cover-art-09": "https://readdy.ai/api/search-image?query=Minimalist%20side-by-side%20comparison%20of%20two%20film%20frames%20on%20a%20light%20table%20in%20a%20dark%20editing%20room%2C%20a%20magnifying%20loupe%20placed%20over%20the%20overlapping%20area%20revealing%20identical%20compositions%2C%20film%20strip%20negatives%20scattered%20around%2C%20dramatic%20focused%20task%20lighting%2C%20investigative%20atmosphere&width=800&height=600&seq=cover-art-09&orientation=landscape",
  "cover-art-10": "https://readdy.ai/api/search-image?query=Minimalist%20scene%20of%20a%20wedding%20photo%20frame%20face-down%20on%20a%20polished%20wooden%20table%2C%20a%20torn%20divorce%20document%20visible%20beside%20it%2C%20a%20gavel%20in%20the%20background%20slightly%20out%20of%20focus%2C%20soft%20window%20light%20creating%20gentle%20shadows%2C%20somber%20and%20reflective%20atmosphere%2C%20editorial%20photography%20in%20muted%20sepia%20and%20charcoal%20tones&width=800&height=600&seq=cover-art-10&orientation=landscape",
  "cover-art-11": "https://readdy.ai/api/search-image?query=Minimalist%20blurred%20nightclub%20interior%20seen%20through%20a%20rain-streaked%20window%2C%20colorful%20neon%20lights%20reflected%20and%20distorted%20on%20the%20wet%20glass%2C%20a%20cocktail%20glass%20silhouette%20visible%20through%20the%20blur%2C%20moody%20and%20mysterious%20night%20atmosphere%2C%20editorial%20photography%20with%20vibrant%20neon%20tones%20against%20dark%20background&width=800&height=600&seq=cover-art-11&orientation=landscape",
  "cover-art-12": "https://readdy.ai/api/search-image?query=Minimalist%20scene%20of%20a%20director%20chair%20standing%20alone%20in%20an%20empty%20dark%20soundstage%2C%20a%20single%20spotlight%20beam%20illuminating%20the%20chair%20from%20above%2C%20the%20chair%20casting%20a%20long%20dramatic%20shadow%2C%20stark%20and%20powerful%20atmosphere%2C%20editorial%20photography%20in%20high%20contrast%20black%20and%20white&width=800&height=600&seq=cover-art-12&orientation=landscape",
};

export default function ArticleCard({
  slug,
  title,
  category,
  coverImageSeq,
  publishedAt,
  views,
  isHot,
  isBreaking,
  variant = "vertical",
}: ArticleCardProps) {
  const imageUrl = coverImageMap[coverImageSeq] || coverImageMap["cover-art-01"];

  if (variant === "horizontal") {
    return (
      <Link
        to={`/article/${slug}`}
        className="group flex gap-4 p-3 rounded-lg bg-background-50 hover:bg-background-100 transition-colors"
      >
        <div className="relative w-24 h-24 md:w-28 md:h-28 rounded-lg overflow-hidden shrink-0">
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
          {isBreaking && (
            <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 text-[10px] font-semibold bg-red-500 text-white rounded">
              爆
            </span>
          )}
        </div>
        <div className="flex flex-col justify-between min-w-0 py-0.5">
          <div>
            <span className="text-[11px] text-accent-600 font-medium">
              {getCategoryName(category)}
            </span>
            <h3 className="text-sm font-semibold text-foreground-950 mt-1 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
              {title}
            </h3>
          </div>
          <div className="flex items-center gap-3 text-[11px] text-foreground-500 mt-1.5">
            <span>{formatTime(publishedAt)}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line text-xs"></i>
              {formatViews(views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${slug}`}
      className="group block rounded-lg overflow-hidden bg-background-50 hover:bg-background-100 transition-all"
    >
      <div className="relative w-full aspect-[4/3] overflow-hidden">
        <img
          src={imageUrl}
          alt={title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        <div className="absolute top-3 left-3 flex gap-1.5">
          {isBreaking && (
            <span className="px-2 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded-full">
              爆
            </span>
          )}
          {isHot && (
            <span className="px-2 py-0.5 text-[10px] font-bold bg-accent-500 text-background-50 rounded-full">
              热
            </span>
          )}
          <span className="px-2 py-0.5 text-[10px] font-medium bg-background-50/20 backdrop-blur-sm text-background-50 rounded-full">
            {getCategoryName(category)}
          </span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 className="text-sm md:text-[15px] font-bold text-white leading-snug line-clamp-2 group-hover:text-primary-200 transition-colors">
            {title}
          </h3>
          <div className="flex items-center gap-3 mt-2 text-[11px] text-white/70">
            <span>{formatTime(publishedAt)}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line text-[11px]"></i>
              {formatViews(views)}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}